import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, Select } from '@/shared/ui'
import {
  integrationsApi,
  ik,
  useAction,
  type Delivery,
  type InboundRequest,
  type Inbound,
  type Webhook,
} from '@/entities/integration'
import { Drawer, Empty, fullTime, JsonView, LogIcon, RetryIcon, SearchIcon, Segmented, StatusPill, timeAgo } from './kit'
import { BitrixRunView } from './BitrixBuilder'
import s from './integrations.module.css'

/* ---------------------------------------------------------- Journal tab */

export function JournalTab({
  hooks,
  inbound,
  webhookId,
  setWebhookId,
}: {
  hooks: Webhook[]
  inbound: Inbound[]
  webhookId: number
  setWebhookId: (v: number) => void
}) {
  const [side, setSide] = useState<'out' | 'in'>('out')
  return (
    <>
      <div className={s.sectionHead}>
        <div>
          <h2 className={s.sectionTitle}>Журнал</h2>
          <p className={s.sectionText}>
            Каждая отправка и каждый входящий запрос за 30 дней: что ушло, что ответил сервер, все попытки. Упавшее можно
            повторить одной кнопкой.
          </p>
        </div>
        <Segmented
          value={side}
          onChange={setSide}
          options={[
            { value: 'out', label: 'Мы отправили' },
            { value: 'in', label: 'Пришло к нам' },
          ]}
        />
      </div>
      {side === 'out' ? (
        <DeliveriesJournal hooks={hooks} webhookId={webhookId} setWebhookId={setWebhookId} />
      ) : (
        <InboundJournal inbound={inbound} />
      )}
    </>
  )
}

function DeliveriesJournal({
  hooks,
  webhookId,
  setWebhookId,
}: {
  hooks: Webhook[]
  webhookId: number
  setWebhookId: (v: number) => void
}) {
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [offset, setOffset] = useState(0)
  const [open, setOpen] = useState<number | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300)
    return () => clearTimeout(t)
  }, [q])
  useEffect(() => setOffset(0), [status, debounced, webhookId])

  const query = useQuery({
    queryKey: [...ik.deliveries, webhookId, status, debounced, offset],
    queryFn: () => integrationsApi.deliveries({ webhook: webhookId || undefined, status, q: debounced, offset }),
    refetchInterval: 10_000,
  })

  const retryAll = useAction(
    () => integrationsApi.retryFailed(webhookId, 24),
    (r) => (r.queued ? `Поставили в очередь: ${r.queued}` : 'Упавших за сутки нет'),
  )

  const items = query.data?.items ?? []
  const total = query.data?.total ?? 0

  return (
    <>
      <div className={s.journalBar}>
        <Select
          value={String(webhookId)}
          onChange={(e) => setWebhookId(Number(e.target.value))}
          options={[{ value: '0', label: 'Все уведомления' }, ...hooks.map((h) => ({ value: String(h.id), label: h.name }))]}
          style={{ maxWidth: 240 }}
        />
        <Segmented
          size="sm"
          value={status}
          onChange={setStatus}
          options={[
            { value: '', label: 'Все' },
            { value: 'success', label: 'Доставлено' },
            { value: 'retrying', label: 'Повтор' },
            { value: 'failed', label: 'Ошибки' },
          ]}
        />
        <div className={s.journalSearch}>
          <SearchIcon />
          <input className="" style={{ width: '100%', height: 32, borderRadius: 10, border: '1px solid var(--border)', paddingRight: 10, font: 'inherit', fontSize: 13 }}
            placeholder="Телефон, событие, ошибка…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <span className={s.spacer} />
        {webhookId > 0 && (
          <Button size="sm" variant="secondary" loading={retryAll.isPending}
            onClick={() => window.confirm('Повторить все упавшие отправки за последние 24 часа?') && retryAll.mutate(undefined)}>
            <RetryIcon /> Повторить упавшие за сутки
          </Button>
        )}
      </div>

      {items.length === 0 && !query.isLoading ? (
        <Empty
          icon={<LogIcon />}
          title={status || debounced ? 'Ничего не нашли' : 'Пока пусто'}
          text={
            status || debounced
              ? 'Попробуйте другой фильтр.'
              : 'Как только случится событие, на которое вы подписаны, отправка появится здесь. Можно нажать «Отправить тест» в настройках уведомления.'
          }
        />
      ) : (
        <div className={s.table}>
          <div className={`${s.row} ${s.rowHead}`}>
            <span>Статус</span>
            <span>Событие</span>
            <span className={s.rowHide}>Куда</span>
            <span className={s.rowHide}>Ответ</span>
            <span className={s.rowHide}>Попыток</span>
            <span>Когда</span>
          </div>
          {items.map((d) => (
            <div key={d.id} className={s.row} onClick={() => setOpen(d.id)}>
              <span>
                <StatusPill status={d.status} />
              </span>
              <span className={s.cellMain}>
                <div className={s.cellTitle}>
                  {d.eventTitle}
                  {d.isTest && <span className={s.chip} style={{ marginLeft: 6, height: 18 }}>тест</span>}
                </div>
                <div className={s.cellSub}>{d.lastError || d.eventId}</div>
              </span>
              <span className={`${s.cellMain} ${s.rowHide}`}>
                <div className={s.cellTitle}>{d.webhookName}</div>
              </span>
              <span className={s.rowHide}>
                <HttpCode code={d.lastStatus} />
                <span className={s.muted}> · {d.durationMs} мс</span>
              </span>
              <span className={s.rowHide}>
                {d.attempts}
                {d.status === 'retrying' && <span className={s.muted}> · след. {timeAgo(d.nextAt)}</span>}
              </span>
              <span className={s.muted} title={fullTime(d.createdAt)}>
                {timeAgo(d.createdAt)}
              </span>
            </div>
          ))}
          <Pager total={total} offset={offset} setOffset={setOffset} />
        </div>
      )}
      {open && <DeliveryDrawer id={open} onClose={() => setOpen(null)} />}
    </>
  )
}

function Pager({ total, offset, setOffset }: { total: number; offset: number; setOffset: (n: number) => void }) {
  if (total <= 30) return null
  return (
    <div className={s.pager}>
      <span>
        {offset + 1}–{Math.min(total, offset + 30)} из {total}
      </span>
      <span style={{ display: 'flex', gap: 6 }}>
        <Button size="sm" variant="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 30))}>
          ← Назад
        </Button>
        <Button size="sm" variant="secondary" disabled={offset + 30 >= total} onClick={() => setOffset(offset + 30)}>
          Дальше →
        </Button>
      </span>
    </div>
  )
}

export function HttpCode({ code }: { code: number }) {
  if (!code) return <span className={`${s.httpCode} ${s.httpBad}`}>—</span>
  return <span className={`${s.httpCode} ${code < 300 ? s.httpOk : s.httpBad}`}>{code}</span>
}

function DeliveryDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const q = useQuery({ queryKey: [...ik.deliveries, 'one', id], queryFn: () => integrationsApi.delivery(id), refetchInterval: 5000 })
  const d = q.data
  return (
    <Drawer title={d ? d.eventTitle : 'Отправка'} subtitle={d ? `${d.webhookName} · ${fullTime(d.createdAt)}` : undefined}
      icon={<LogIcon />} onClose={onClose}>
      {d ? <DeliveryDetails delivery={d} /> : <span className={s.muted}>Загружаем…</span>}
    </Drawer>
  )
}

/** Подробности отправки — и в журнале, и после «Отправить тест». */
export function DeliveryDetails({ delivery: d, compact }: { delivery: Delivery; compact?: boolean }) {
  const retry = useAction(() => integrationsApi.retryDelivery(d.id), 'Отправили ещё раз')
  const bitrix = d.request?.method === 'BITRIX'
  let bitrixRun: unknown = null
  if (bitrix && d.responseBody) {
    try {
      bitrixRun = JSON.parse(d.responseBody)
    } catch {
      bitrixRun = null
    }
  }
  return (
    <div className={s.resultBox}>
      <div className={s.resultHead}>
        <StatusPill status={d.status} />
        {!bitrix && <HttpCode code={d.lastStatus} />}
        <span className={s.muted}>{d.durationMs} мс</span>
        <span className={s.spacer} />
        {!d.isTest && d.status !== 'pending' && d.status !== 'sending' && (
          <Button size="sm" variant="secondary" loading={retry.isPending} onClick={() => retry.mutate(undefined)}>
            <span className={s.btnRow}><RetryIcon /> Повторить</span>
          </Button>
        )}
      </div>
      <div className={s.resultBody}>
        {d.lastError && <div className={`${s.alert} ${s.alertBad}`}>{d.lastError}</div>}
        {d.status === 'success' && !d.lastError && (
          <div className={`${s.alert} ${s.alertOk}`}>{bitrix ? 'Шаги в Битриксе выполнены' : 'Сервер принял запрос'}</div>
        )}
        {bitrix && bitrixRun ? (
          <BitrixRunView result={{ run: bitrixRun as never, error: '', durationMs: d.durationMs, sample: false, live: true }} />
        ) : null}
        {!bitrix && d.request?.url && (
          <dl className={s.kvTable}>
            <dt>Запрос</dt>
            <dd className={s.mono}>
              {d.request.method} {d.request.url}
            </dd>
            {d.request.headers?.length ? (
              <>
                <dt>Заголовки</dt>
                <dd className={s.mono}>
                  {d.request.headers.map((h) => (
                    <div key={h.key}>
                      {h.key}: {h.value}
                    </div>
                  ))}
                </dd>
              </>
            ) : null}
          </dl>
        )}
        <div className={compact ? s.split : undefined} style={compact ? undefined : { display: 'flex', flexDirection: 'column', gap: 10 }}>
          {!bitrix && d.request?.body && (
            <div className={s.formRow}>
              <span className={s.muted}>Что ушло</span>
              <JsonView value={d.request.body} maxHeight={compact ? 240 : 360} />
            </div>
          )}
          {!bitrix && (
            <div className={s.formRow}>
              <span className={s.muted}>Что ответил ваш сервер</span>
              {d.responseBody ? <JsonView value={d.responseBody} maxHeight={compact ? 240 : 360} /> : <span className={s.muted}>пустой ответ</span>}
            </div>
          )}
        </div>
        {!compact && d.history && d.history.length > 0 && (
          <div className={s.formRow}>
            <span className={s.muted}>Попытки</span>
            <div className={s.timeline}>
              {d.history.map((a, i) => (
                <div key={i} className={`${s.tlItem} ${a.status >= 200 && a.status < 300 && !a.error ? s.tlOk : s.tlBad}`}>
                  <div className={s.tlTitle}>
                    {a.manual ? 'Повтор вручную' : `Попытка ${i + 1}`} · <HttpCode code={a.status} /> · {a.durationMs} мс
                  </div>
                  <div className={s.tlSub}>
                    {fullTime(a.at)}
                    {a.error ? ` — ${a.error}` : ''}
                  </div>
                </div>
              ))}
              {d.status === 'retrying' && (
                <div className={s.tlItem}>
                  <div className={s.tlTitle}>Следующая попытка {timeAgo(d.nextAt)}</div>
                  <div className={s.tlSub}>Повторяем через 1 мин, 5 мин, 30 мин и 2 часа</div>
                </div>
              )}
            </div>
          </div>
        )}
        {!compact && d.payload ? (
          <details>
            <summary className={s.addLink} style={{ cursor: 'pointer' }}>Исходное событие</summary>
            <div style={{ marginTop: 8 }}>
              <JsonView value={d.payload} />
            </div>
          </details>
        ) : null}
      </div>
    </div>
  )
}

/* ----------------------------------------------------------- Inbound */

function InboundJournal({ inbound }: { inbound: Inbound[] }) {
  const [id, setId] = useState(0)
  const [status, setStatus] = useState('')
  const [offset, setOffset] = useState(0)
  const [open, setOpen] = useState<InboundRequest | null>(null)
  useEffect(() => setOffset(0), [id, status])
  const q = useQuery({
    queryKey: [...ik.inboundRequests, id, status, offset],
    queryFn: () => integrationsApi.inboundRequests({ inbound: id || undefined, status, offset }),
    refetchInterval: 10_000,
  })
  const items = q.data?.items ?? []
  const names = Object.fromEntries(inbound.map((x) => [x.id, x.name]))
  return (
    <>
      <div className={s.journalBar}>
        <Select value={String(id)} onChange={(e) => setId(Number(e.target.value))} style={{ maxWidth: 240 }}
          options={[{ value: '0', label: 'Все ссылки' }, ...inbound.map((x) => ({ value: String(x.id), label: x.name }))]} />
        <Segmented size="sm" value={status} onChange={setStatus}
          options={[
            { value: '', label: 'Все' },
            { value: 'ok', label: 'Успешно' },
            { value: 'partial', label: 'Частично' },
            { value: 'rejected', label: 'Отклонено' },
          ]} />
      </div>
      {items.length === 0 && !q.isLoading ? (
        <Empty icon={<LogIcon />} title="Запросов ещё не было"
          text="Когда ваша система отправит данные на входящую ссылку, запрос появится здесь — с телом, результатом и ошибками по каждой записи." />
      ) : (
        <div className={s.table}>
          <div className={`${s.row} ${s.rowHead}`}>
            <span>Статус</span>
            <span>Результат</span>
            <span className={s.rowHide}>Ссылка</span>
            <span className={s.rowHide}>Ответ</span>
            <span className={s.rowHide}>IP</span>
            <span>Когда</span>
          </div>
          {items.map((r) => (
            <div key={r.id} className={s.row} onClick={() => setOpen(r)}>
              <span>
                <StatusPill status={r.status} />
              </span>
              <span className={s.cellMain}>
                <div className={s.cellTitle}>{r.message}</div>
                <div className={s.cellSub}>
                  {r.result ? `обработано ${r.result.processed}, ошибок ${r.result.failed}` : r.contentType}
                </div>
              </span>
              <span className={`${s.cellMain} ${s.rowHide}`}>
                <div className={s.cellTitle}>{names[r.inboundId] ?? '—'}</div>
              </span>
              <span className={s.rowHide}>
                <HttpCode code={r.httpStatus} />
              </span>
              <span className={`${s.rowHide} ${s.mono}`}>{r.ip}</span>
              <span className={s.muted} title={fullTime(r.createdAt)}>
                {timeAgo(r.createdAt)}
              </span>
            </div>
          ))}
          <Pager total={q.data?.total ?? 0} offset={offset} setOffset={setOffset} />
        </div>
      )}
      {open && (
        <Drawer title={open.message} subtitle={`${names[open.inboundId] ?? ''} · ${fullTime(open.createdAt)}`} icon={<LogIcon />}
          onClose={() => setOpen(null)}>
          <InboundResultView result={open.result} status={open.status} httpStatus={open.httpStatus} />
          <div className={s.formRow}>
            <span className={s.muted}>Что прислали (секреты скрыты)</span>
            <JsonView value={open.body} />
          </div>
        </Drawer>
      )}
    </>
  )
}

export function InboundResultView({
  result,
  status,
  httpStatus,
}: {
  result: import('@/entities/integration').InboundResult | null
  status: string
  httpStatus?: number
}) {
  if (!result) return null
  return (
    <div className={s.resultBox}>
      <div className={s.resultHead}>
        <StatusPill status={status} />
        {httpStatus ? <HttpCode code={httpStatus} /> : null}
        <span>{result.message}</span>
      </div>
      {result.results && result.results.length > 0 && (
        <div className={s.resultBody}>
          {result.results.slice(0, 50).map((it) => (
            <div key={it.index} className={s.flowStep}>
              <span className={s.flowStepIdx}>{it.index + 1}</span>
              <div className={s.flowStepMain}>
                <div className={s.flowStepTitle}>{it.phone || `Запись ${it.index + 1}`}</div>
                <div className={s.flowStepDetail}>
                  {it.ok ? it.did : it.error}
                  {it.fields?.map((f) => (
                    <div key={f.path}>
                      <b>{f.path}</b>: {f.message}
                    </div>
                  ))}
                </div>
              </div>
              <StatusPill status={it.ok ? (status === 'dry_run' ? 'planned' : 'ok') : 'failed'} />
            </div>
          ))}
          {result.results.length > 50 && <span className={s.muted}>и ещё {result.results.length - 50}</span>}
        </div>
      )}
    </div>
  )
}
